import React from 'react';

import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CodeSelector from './CodeSelector';

import useCodesSidebar from 'hooks/codes/useCodesSidebar';
import { v4 as uuidv4 } from 'uuid';

// import handleMultiSelectChange from './handleMultiSelectChange';
import { useAddCodeToExcerpt, useRemoveCodeFromExcerpt } from 'hooks/excerpts/mutations';
import { useCreateCode } from 'hooks/codes/mutations/useCreateCode';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import useTrackingPage from 'hooks/useTrackingPage';

const CodeMultiSelect = (props) => {
    const { excerptCodeIds, projectID, excerptId, placement } = props;
    const addCodeButtonRef = React.useRef(null);
    const [codeSelectorIsOpen, setCodeSelectorIsOpen] = React.useState(false);
    const [selectedCodes, setSelectedCodes] = React.useState([]);
    const [options, setOptions] = React.useState([]);
    const { codes, isLoading: codesLoading } = useCodesSidebar(codeSelectorIsOpen ? projectID : null);
    const addCodeToExcerptMutation = useAddCodeToExcerpt();
    const createCodeMutation = useCreateCode();
    const removeCodeFromExcerptMutation = useRemoveCodeFromExcerpt();
    // codeOperations supplies the text-selection fallbacks (addCodeToExcerpt/
    // onNewCode) used on the comparison page when there is no excerpt yet.
    const codeOperations = React.useContext(CodeOperationsContext);
    // Resolve page from the URL at action time; surface is intrinsic to
    // this component (the inline + on an excerpt).
    const page = useTrackingPage();
    const trackingContext = { page, surface: 'excerpt_inline_dropdown' };

    /**
     * Handles changes in code selection for excerpts.
     * 
     * Three scenarios:
     * 
     * Scenario 1: Add - Adding an existing code (object)
     * - When selectedCodes.length > excerptCodes.length and the added item has an ID
     * - Example: Adding { id: "code3", name: "Critical" } to selection
     * - Action: Associate this existing code with the excerpt
     * 
     * Scenario 2: Create - Adding a new code (string)
     * - When selectedCodes.length > excerptCodes.length and the added item is a string
     * - Example: Adding "New Code Label" to selection
     * - Action: Create new code first, then associate with excerpt
     * 
     * Scenario 3: Remove - Removing a code
     * - When selectedCodes.length < excerptCodes.length
     * - Example: Removing { id: "code2", name: "Review" } from selection
     * - Action: Disassociate this code from the excerpt
     * 
     * Data structure examples:
     * 
     * Default: Array of objects with id property
     * selectedCodes = [
     *   { id: "code1", name: "Important" },
     *   { id: "code2", name: "Review" }
     * ]
     * 
     * With new string code:
     * selectedCodes = [
     *   { id: "code1", name: "Important" },
     *   { id: "code2", name: "Review" },
     *   "New Code Name"  // String for new code
     * ]
     * 
     * Data structure examples:
     * 
     * Default: Array of objects with id property
     * selectedCodes = [
     *   { id: "code1", name: "Important" },
     *   { id: "code2", name: "Review" }
     * ]
     * 
     * New scenario: Array with mixed elements
     * newSelectedCodes = [
     *   { id: "code1", name: "Important" },
     *   "New Code Label",  // String instead of object with id
     *   { name: "Urgent", color: "red" }  // Object without id
     * ]
     * 
     * @param {Array} newSelectedCodes - The updated selection of codes
     * @param {Array} selectedCodes - The previous selection of codes
     * @param {string} excerptId - The ID of the current excerpt
     * @param {string} projectID - The ID of the current project
     */
    // Handle selection changes (add/remove codes)
    const handleMultiSelectChange = (newSelectedCodes, selectedCodes, excerptId, projectID) => {
        if (newSelectedCodes.length > selectedCodes.length) {
        const addedItem = newSelectedCodes.find(
            item => !selectedCodes.some(code => code.id === item.id)
        );
        // Scenario 1: Add an existing code (object)
        if (addedItem?.id) {
            if (excerptId) {
                addCodeToExcerptMutation.mutate({ codeId: addedItem.id, excerptId, trackingContext });
            } else if (codeOperations?.addCodeToExcerpt) {
                // Comparison page: the user has selected text but no excerpt exists
                // yet. CodeOperationsContext creates the excerpt from the current
                // text selection, then attaches this existing code to it.
                codeOperations.addCodeToExcerpt(addedItem.id, null, trackingContext);
            }
        // Scenario 2: Create and add a new code (string)
        } else {
            const newCodeName = addedItem.inputValue || addedItem;
            if (excerptId) {
                const clientCodeId = uuidv4();
                createCodeMutation.mutateAsync({
                    name: newCodeName,
                    projectId: projectID,
                    client_id: clientCodeId,
                    trackingContext,
                })
                addCodeToExcerptMutation.mutate({ codeId: clientCodeId, excerptId, trackingContext })
            } else if (codeOperations?.onNewCode) {
                // Comparison page: the user has selected text but no excerpt exists
                // yet. CodeOperationsContext creates both the new code and an excerpt
                // from the current text selection, then attaches the code to it.
                codeOperations.onNewCode(newCodeName, null, trackingContext);
            }
        }
        // Scenario 3: Remove a code
        } else if (newSelectedCodes.length < selectedCodes.length) {
        const removedItem = selectedCodes.find(
            item => !newSelectedCodes.some(code => code.id === item.id)
        );
        if (removedItem) removeCodeFromExcerptMutation.mutate({ codeId: removedItem.id, excerptId, trackingContext });
        }
    };

    React.useEffect(() => {
        // if not open, do not fetch codes
        if (!codeSelectorIsOpen) {
            return;
        }

        const handleCodesChange = () => {
            const codesWithSelected = codes.map((code) => {
                const selected = excerptCodeIds.includes(code.id);
                return { ...code, selected };
            });
            setOptions(codesWithSelected);
            setSelectedCodes(codesWithSelected.filter((code) => code.selected));
        };

        handleCodesChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [String(excerptCodeIds), projectID, codeSelectorIsOpen]);

    // Memoize the handler to prevent unnecessary re-renders
    const handleSelectionChange = React.useCallback((values) => {
        handleMultiSelectChange(values, selectedCodes, excerptId, projectID);
    }, [selectedCodes, excerptId, projectID]);

    return (
        <span
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <IconButton
                id='code-searcher'
                ref={addCodeButtonRef}
                onClick={() => {
                    setCodeSelectorIsOpen(() => !codeSelectorIsOpen);
                }}
                sx={{
                    backgroundColor: '#F0F0F0',
                    height: '19px',
                    width: '19px',
                    fontColor: 'black',
                    marginRight: '12px',
                }}
            >
                <AddIcon />
            </IconButton>
            <CodeSelector
                {...props}
                multiSelect
                selectedCodes={selectedCodes}
                handleSelectionChange={handleSelectionChange}
                options={options}
                anchorRef={addCodeButtonRef.current}
                toggleRef={addCodeButtonRef.current}
                open={codeSelectorIsOpen}
                setOpen={setCodeSelectorIsOpen}
                allowNewCodes
                placement={placement}
                title="Add Codes to Snippet"
            />
        </span>
    );
}

export default CodeMultiSelect;